import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { siteIntro } from '@/mocks/site';

const values = [
  { icon: 'ri-shield-check-line', title: '权威可信', desc: '严选信息来源，坚持内容审核，为用户提供可靠、真实的资讯与服务信息。' },
  { icon: 'ri-timer-flash-line', title: '及时高效', desc: '7×24 小时更新机制，热点动态快速触达，让你第一时间掌握身边与天下事。' },
  { icon: 'ri-service-line', title: '实用便捷', desc: '覆盖新闻、生活、娱乐多元场景，让信息获取与生活服务一站触达。' },
  { icon: 'ri-user-heart-line', title: '用户为先', desc: '持续倾听用户声音，用更好的产品体验回馈每一位关注 91网站的朋友。' },
];

const milestones = [
  { year: '2019', text: '91网站正式上线，以新闻资讯频道起步，服务首批用户。' },
  { year: '2021', text: '上线生活服务频道，聚合家政、维修、搬家等便民服务资源。' },
  { year: '2023', text: '娱乐内容频道上线，覆盖影视、音乐、游戏与综艺热点。' },
  { year: '2026', text: '完成全站体验升级，形成资讯、服务、娱乐三位一体的综合门户。' },
];

export default function AboutPage() {
  useDocumentMeta({
    title: '关于我们 - 91网站',
    description:
      '91网站是覆盖新闻资讯、生活服务与娱乐内容的综合门户平台，秉持权威、及时、实用的理念，为用户提供可信赖的信息与便捷的在线服务。',
    keywords: '91网站,关于我们,综合门户,品牌故事',
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb items={[{ label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于 91网站</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-secondary-600">{siteIntro.desc}</p>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {siteIntro.stats.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
              <div className="font-heading text-2xl font-bold text-primary-500 md:text-3xl">{stat.value}</div>
              <div className="mt-1 text-xs text-secondary-500 md:text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-flag-2-line text-accent-500"></i>
              品牌故事
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700">
              <p>
                91网站诞生于一个朴素的初衷：让普通人用一个网站，就能看到重要的事、找到有用的服务、获得轻松的娱乐。
                从最初单一的信息频道，到如今覆盖新闻、生活、娱乐的综合门户，我们始终围绕“信息获取效率”打磨产品。
              </p>
              <p>
                我们相信，一个好的门户网站不应让人眼花缭乱，而应让信息层次清晰、服务触手可及。因此在内容组织上，
                我们坚持分类清晰、重点突出；在服务体验上，我们力求简洁直观、易于上手。
              </p>
              <p>
                未来，91网站将继续深耕内容品质与服务能力，努力成为用户值得长期信赖的信息伙伴。
              </p>
            </div>
          </div>

          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-team-line text-accent-500"></i>
              团队介绍
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700">
              <p>
                91网站由一支热爱内容与产品的团队共同打造，成员来自媒体、互联网产品与技术服务等不同领域。
                我们既有对内容品质的坚持，也有对技术体验的追求。
              </p>
              <p>
                团队下设内容编辑、服务运营、产品设计与技术研发四个小组，相互协作，
                从选题策划到服务对接，每一环都力求专业严谨。
              </p>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { icon: 'ri-edit-2-line', label: '内容编辑组' },
                { icon: 'ri-hand-heart-line', label: '服务运营组' },
                { icon: 'ri-palette-line', label: '产品设计组' },
                { icon: 'ri-code-s-slash-line', label: '技术研发组' },
              ].map((t) => (
                <div
                  key={t.label}
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-3 text-sm text-foreground-800"
                >
                  <i className={`${t.icon} text-primary-500`}></i>
                  {t.label}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-focus-3-line text-accent-500"></i>
            我们的坚持
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-secondary-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-history-line text-accent-500"></i>
            发展历程
          </h2>
          <div className="mt-5 space-y-4">
            {milestones.map((m, i) => (
              <div key={m.year} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      i === milestones.length - 1
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-primary-100 text-primary-700'
                    }`}
                  >
                    {m.year}
                  </span>
                  {i < milestones.length - 1 && <span className="mt-1 w-px flex-1 bg-background-300"></span>}
                </div>
                <p className="flex-1 rounded-lg border border-background-200 bg-background-50 p-4 text-sm leading-relaxed text-foreground-700">
                  {m.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-xl bg-secondary-950 p-6 md:p-10">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-background-50">
            <i className="ri-copyright-line text-accent-400"></i>
            版权与免责声明
          </h2>
          <div className="mt-4 space-y-3 text-xs leading-relaxed text-secondary-300 md:text-sm">
            <p>
              1. 本站部分内容为综合整理或演示示例，仅用于产品展示与信息参考，不代表任何官方立场，亦不构成投资、
              医疗、法律等专业建议。
            </p>
            <p>
              2. 用户在浏览与使用本站服务时，请以权威部门、正规机构发布的信息为准，因使用本站信息而产生的后果，
              本站不承担相应责任。
            </p>
            <p>
              3. 本站刊载的文字、图片等内容，其知识产权归相应权利人所有。如有内容涉及版权问题，请通过
              联系我们页面与我们取得联系，我们将及时处理。
            </p>
            <p>4. 本站的版权归 91网站所有，未经许可不得擅自转载或用于商业用途。</p>
          </div>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-mail-send-line"></i>
            联系我们
          </Link>
        </section>
      </div>
    </SiteLayout>
  );
}